export enum MerchantPricingStrategyType {
  BASE_PRICE = 'BASE_PRICE',
  MIN_PRICE = 'MIN_PRICE',
  DRAFT = 'DRAFT'
}

export enum MerchantPricingStrategyStatus {
  ACTIVE = 'ACTIVE',
  DRAFT = 'DRAFT'
}

type PriceStrategy = {
  type: MerchantPricingStrategyType
  min_amount?: number
  base_price?: number
}

export enum MeterType {
  COD_ORDER = 'COD_ORDER',
  ONLINE_ORDER = 'ONLINE_ORDER',
  COD_RETURN = 'COD_RETURN',
  ONLINE_RETURN = 'ONLINE_RETURN',
  SSO = 'SSO',
  CART = 'CART'
}

export type Meter = {
  type: MeterType
  multiplier: number
}

export enum MerchantPricingFeature {
  ACTIVATE_REWARDS = 'ACTIVATE_REWARDS',
  GROUP_DISCOUNTS = 'GROUP_DISCOUNTS',
  TAB_OVERVIEW = 'TAB_OVERVIEW',
  VIEW_OVERVIEW = 'VIEW_OVERVIEW',
  DOWNLOAD_REPORT_OVERVIEW = 'DOWNLOAD_REPORT_OVERVIEW',

  // Orders permissions
  TAB_ORDERS = 'TAB_ORDERS',
  VIEW_ORDERS = 'VIEW_ORDERS',
  EDIT_ORDERS = 'EDIT_ORDERS',
  CANCEL_ORDERS = 'CANCEL_ORDERS',
  REFUND_ORDERS = 'REFUND_ORDERS',
  EXPORT_ORDER_DATA = 'EXPORT_ORDER_DATA',
  SHOW_PII_DATA = 'SHOW_PII_DATA',

  // Abandoned Checkouts permissions
  TAB_ABANDONED_CHECKOUTS = 'TAB_ABANDONED_CHECKOUTS',
  VIEW_ABC_TAB = 'VIEW_ABC_TAB',
  EXIT_FORM_CONFIG = 'EXIT_FORM_CONFIG',
  EXPORT_ABC_DATA = 'EXPORT_ABC_DATA',

  // Upsell permissions
  TAB_UPSELL = 'TAB_UPSELL',
  TAB_UPSELL_LITE = 'TAB_UPSELL_LITE',
  VIEW_UPSELL = 'VIEW_UPSELL',
  CREATE_UPSELL = 'CREATE_UPSELL',
  EDIT_UPSELL = 'EDIT_UPSELL',
  ENABLE_UPSELL = 'ENABLE_UPSELL',
  DELETE_UPSELL = 'DELETE_UPSELL',
  VIEW_UPSELL_ANALYTICS = 'VIEW_UPSELL_ANALYTICS',
  DISABLE_UPSELL = 'DISABLE_UPSELL',

  // Discounts permissions
  TAB_DISCOUNTS = 'TAB_DISCOUNTS',
  VIEW_DISCOUNTS = 'VIEW_DISCOUNTS',
  CREATE_DISCOUNTS = 'CREATE_DISCOUNTS',
  ACTIVATE_DISCOUNTS = 'ACTIVATE_DISCOUNTS',
  DELETE_DISCOUNTS = 'DELETE_DISCOUNTS',

  // Automations permissions
  TAB_AUTOMATIONS = 'TAB_AUTOMATIONS',
  VIEW_AUTOMATIONS = 'VIEW_AUTOMATIONS',
  CREATE_AUTOMATIONS = 'CREATE_AUTOMATIONS',
  ACTIVATE_AUTOMATIONS = 'ACTIVATE_AUTOMATIONS',
  DELETE_AUTOMATIONS = 'DELETE_AUTOMATIONS',

  // Payment Offers permissions
  TAB_PAYMENT_OFFERS = 'TAB_PAYMENT_OFFERS',
  VIEW_PAYMENT_OFFERS = 'VIEW_PAYMENT_OFFERS',
  CREATE_PAYMENT_OFFERS = 'CREATE_PAYMENT_OFFERS',
  ACTIVATE_PAYMENT_OFFERS = 'ACTIVATE_PAYMENT_OFFERS',
  DELETE_PAYMENT_OFFERS = 'DELETE_PAYMENT_OFFERS',

  // Rewards permissions
  TAB_REWARDS = 'TAB_REWARDS',
  VIEW_REWARDS_SETUP = 'VIEW_REWARDS_SETUP',
  EDIT_REWARDS_SETUP = 'EDIT_REWARDS_SETUP',
  ENABLE_REWARDS = 'ENABLE_REWARDS',
  VIEW_REWARDS_CUSTOMISATION = 'VIEW_REWARDS_CUSTOMISATION',
  EDIT_REWARDS_CUSTOMISATION = 'EDIT_REWARDS_CUSTOMISATION',
  VIEW_REWARDS_ANALYTICS = 'VIEW_REWARDS_ANALYTICS',

  // Shipping & Delivery permissions
  TAB_SHIPPING_DELIVERY_V2 = 'TAB_SHIPPING_DELIVERY_V2',
  VIEW_SHIPPING_DELIVERY = 'VIEW_SHIPPING_DELIVERY',
  EDIT_SHIPPING_DELIVERY = 'EDIT_SHIPPING_DELIVERY',
  MANAGE_RATES_VIEW = 'MANAGE_RATES_VIEW',
  MANAGE_RATES_EDIT = 'MANAGE_RATES_EDIT',

  // RTO Suite permissions
  TAB_RTO_SUITE = 'TAB_RTO_SUITE',
  VIEW_RTO_SUITE = 'VIEW_RTO_SUITE',
  EDIT_RTO_SUITE = 'EDIT_RTO_SUITE',

  // Customise UI permissions
  TAB_CUSTOMISE_UI = 'TAB_CUSTOMISE_UI',
  VIEW_CUSTOMISE_UI = 'VIEW_CUSTOMISE_UI',
  EDIT_CUSTOMISE_UI = 'EDIT_CUSTOMISE_UI',
  CUSTOMIZE_BANNER_PAYMENT = 'Customise Banner on Payments Page',
  CUSTOMIZE_PAYMENT_METHODS = 'CUSTOMIZE_PAYMENT_METHODS',
  CUSTOMISE_COUPONS_ADDRESS = 'CUSTOMISE_COUPONS_ADDRESS',
  CUSTOMISE_COUPONS_LOGIN = 'CUSTOMISE_COUPONS_LOGIN',
  CUSTOMIZE_BANNER_ADDRESS = 'CUSTOMIZE_BANNER_ADDRESS',
  CUSTOMIZE_BANNER_LOGIN = 'CUSTOMIZE_BANNER_LOGIN',
  CUSTOMISE_COUPONS_PAYMENT = 'Customise Coupons on Payments Page',
  TRUST_BADGES = 'Edit Trust Badges',
  CHANGE_DESCRIPTION = 'Change description',

  // Cart permissions
  TAB_CART = 'TAB_CART',
  VIEW_CART = 'VIEW_CART',
  EDIT_CART = 'EDIT_CART',

  // Settings permissions
  TAB_SETTINGS = 'TAB_SETTINGS',
  VIEW_SETTINGS = 'VIEW_SETTINGS',
  EDIT_SETUP_SETTINGS = 'EDIT_SETUP_SETTINGS',
  EDIT_SHOP_SETTINGS = 'EDIT_SHOP_SETTINGS',
  EDIT_ACCOUNTS_SETTINGS = 'EDIT_ACCOUNTS_SETTINGS',
  MULTIPLE_FB_CAPI = 'MULTIPLE_FB_CAPI',
  MANAGE_INVOICE = 'MANAGE_INVOICE',
  AUTO_PG_ROUTING = 'AUTO_PG_ROUTING',
  ADD_CUSTOM_ROLE = 'ADD_CUSTOM_ROLE',
  MULTIPLE_PG_SUPPORT = 'MULTIPLE_PG_SUPPORT',
  CHANNEL = 'CHANNEL',
  ACR_EVENT_WEBHOOK = 'ACR webhook',

  // Support permissions
  TAB_SUPPORT = 'Support',

  // Partners permissions
  TAB_PARTNERS = 'TAB_PARTNERS',

  // One Click Addon permissions
  TAB_ONE_CLICK_ADDON = 'TAB_ONE_CLICK_ADDON',
  VIEW_ONE_CLICK_ADDON = 'VIEW_ONE_CLICK_ADDON',
  EDIT_ONE_CLICK_ADDON = 'EDIT_ONE_CLICK_ADDON',
  DELETE_ONE_CLICK_ADDON = 'DELETE_ONE_CLICK_ADDON',
  DISABLE_ONE_CLICK_ADDON = 'DISABLE_ONE_CLICK_ADDON',

  // App Store permissions
  TAB_APP_STORE = 'TAB_APP_STORE',
  APP_STORE_VIEW = 'APP_STORE_VIEW',
  APP_STORE_INSTALL = 'APP_STORE_INSTALL',
  APP_STORE_UNINSTALL = 'APP_STORE_UNINSTALL',

  // SSO permissions
  TAB_SSO = 'TAB_SSO',
  VIEW_SSO_SETUP = 'VIEW_SSO_SETUP',
  VIEW_SSO_ANALYTICS = 'VIEW_SSO_ANALYTICS',
  SHOPPASS_REPORT_TAB = 'SHOPPASS_REPORT_TAB',

  // Reports permissions
  TAB_REPORTS = 'TAB_REPORTS',
  REPORTS_ORDERS = 'REPORTS_ORDERS',
  REPORTS_ACR = 'REPORTS_ACR',
  REPORTS_CONVERSION_FUNNEL = 'REPORTS_CONVERSION_FUNNEL',
  REPORTS_DISCOUNTS = 'REPORTS_DISCOUNTS',

  // Download Reports permissions
  TAB_DOWNLOAD_REPORTS = 'TAB_DOWNLOAD_REPORTS',

  // Analytics permissions
  TAB_ANALYTICS = 'TAB_ANALYTICS',

  // Testing permissions
  TESTING_PERMISSION = 'TESTING_PERMISSION'
}

export type Feature = {
  type: MerchantPricingFeature
  price: number
  /** Absent when the backend sends no label for this feature; render `type` as the fallback. */
  displayName?: string
  /** Null or absent when the backend reports no discount on this feature. */
  discountedPrice?: number | null
}

/** GET /pricing/merchant/credits/balance. Amounts are in rupees; the backend reports 0 on failure. */
export type CreditBalanceResponse = {
  data?: {
    balance?: number
    currency?: string
  }
}

export type MerchantPricingConfig = {
  strategy: PriceStrategy
  meters: Meter[]
  features: Feature[]
}

export enum InvoiceStatus {
  DRAFT = 'draft',
  SENT = 'sent',
  PAID = 'paid',
  OVERDUE = 'overdue'
}

export type Invoice = {
  invoiceNumber: string
  invoiceId: string
  cfSfMerchantId: string
  date: string
  dueDate: string
  invoiceUrl: string
  subTotal: number
  taxTotal: number
  total: number
  currentSubStatus: InvoiceStatus
  paymentLink: string
}

export type MerchantInvoice = {
  invoices: Invoice[]
  paginate: {
    totalCount: number
    pageSize: number
    maxPages: number
  }
}

export type GenerateInvoiceResponse = {
  finalBill: number
  credits: number
  totalBilledAmount: number
  subscriptionInvoice: SubscriptionInvoice
  discounts: BillingDiscounts[]
}

export type SubscriptionInvoice = Array<{
  totalBilledAmount: number
  meterInvoice: {
    codOrder: Array<{
      billedAmount: number
      meterStrategy?: {
        type: 'TIER' | 'BASE_PRICE'
        tiers: Array<{
          fixedPrice: number
          limit: number
          multiplier: number
        }>
      }
    }>
    onlineOrder: Array<{
      billedAmount: number
      meterStrategy?: {
        type: 'TIER' | 'BASE_PRICE'
        tiers: Array<{
          fixedPrice: number
          limit: number
          multiplier: number
        }>
      }
    }>
  }
}>

export type PricingResponse = {
  subscription: {
    strategy: {
      type: 'BASE_PRICE' | 'MIN_PRICE' | 'DRAFT'
      basePrice: number
    }
    status: MerchantPricingStrategyStatus
  }
  meters: PricingMeter[]
  features: Feature[]
}

export type PricingMeter = {
  type: MeterType
  strategy: FlatStrategy | StairStrategy | TierStrategy | NoneStrategy
}

export type FlatStrategy = {
  type: 'FLAT'
  multiplier: number
  fixedValue: number
}

export type StairStrategy = {
  type: 'STAIR'
  stairs: Stair[]
}

export type TierStrategy = {
  type: 'TIER'
  tiers: Tier[]
}

export type NoneStrategy = {
  type: 'NONE'
}

export type Stair = {
  fixedValue: number
  from: number
  to: number
  multiplier: number
}

export type Tier = {
  fixedValue: number
  limit: number
  multiplier: number
}

export type MerchantInvoiceResponseType = {
  data: {
    data: InvoiceResponse[]
    max_pages: number
    page_size: number
    total_count: number
  }
}

export type InvoiceResponse = {
  invoice_number: string
  invoice_id: string
  cf_sf_merchant_id: string
  date: string
  due_date: string
  invoice_url: string
  sub_total: number
  tax_total: number
  total: number
  current_sub_status: InvoiceStatus
  payment_link: string
}

export type GenerateInvoiceResponseBackendType = {
  final_bill: number
  credits: number
  total_billed_amount: number
  subscriptions_invoice: SubscriptionInvoiceBackendType[]
  discounts: BillingDiscountsBackendType[]
}

export type BillingDiscountsBackendType = {
  amount: number
}

export type BillingDiscounts = {
  amount: number
}

export type SubscriptionInvoiceBackendType = {
  total_amount: number
  meter_invoice: {
    COD_ORDER: MeterInvoiceBackendType[]
    ONLINE_ORDER: MeterInvoiceBackendType[]
  }
}

export type MeterInvoiceBackendType = {
  billed_amount: number
  meter_strategy: {
    type: 'TIER' | 'BASE_PRICE'
    stairs: StairBackendType[]
    tiers: TierBackendType[]
  }
}

export type PricingStrategyBackendType =
  | {
      type: 'BASE_PRICE'
      base_price: number
    }
  | {
      type: 'MIN_PRICE'
      min_amount: number
    }
  | {
      type: 'DRAFT'
    }

export type PricingResponseBackendType = {
  data: {
    subscription: {
      strategy: PricingStrategyBackendType
      status: MerchantPricingStrategyStatus
    }
    meters: PricingMeterBackendType[]
    features: FeatureBackendType[]
  }
}

export type PricingMeterBackendType = {
  type: MeterType
  strategy: {
    type: 'FLAT' | 'TIER'
    multiplier: number
    stairs: StairBackendType[]
  }
}

export type FeatureBackendType = {
  feature_id: MerchantPricingFeature
  price: number
  /**
   * `price` after any active merchant discount, on the same monthly basis as `price`.
   * Explicitly null when the response has not been decorated with discount data.
   */
  discounted_price?: number | null
  /** Human label for `feature_id`, e.g. TAB_UPSELL -> "Upsell". Null when the backend has none. */
  display_name?: string | null
  permission?: {
    display_name: string
  }
}

export type StairBackendType = {
  fixed_value: number
  from: number
  to: number
  multiplier: number
}

export type TierBackendType = {
  fixed_value: number
  limit: number
  multiplier: number
}

export type PocDetails = {
  pocName: string
  pocEmail: string
  pocPhone: string
  isPrimaryPoc: boolean
}

export type TransformedTierType = {
  minOrder: number
  maxOrder: number
  flatPrice: number
}

export interface ChurnSwitchDialogProps {
  isOpen: boolean
  setIsOpen: (value: boolean) => void
}

export type ReasonDetailsType = {
  churn_reason: string
  churn_review_meeting_id: string
  churn_reason_comments: string
}

export type ChurnAnalyticsResponse = {
  abandoned_gmv: number
  checkout_initiated: string
  completed_gmv: string
  coupon_count: string
  prefilled_address_percentage: string
  recovered_gmv: number
}

export enum ChurnSwitchDialogState {
  CHURN_NUDGE = 'CHURN_NUDGE',
  CALENDLY_NUDGE = 'CALENDLY_NUDGE',
  CONFIRM_NUDGE = 'CONFIRM_NUDGE',
  CHECKOUT_REACTIVATE = 'CHECKOUT_REACTIVATE'
}
